import { PencilIcon } from '@heroicons/react/24/solid';
import { Button, ButtonProps } from '@praetorian-chariot/ui';
import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { Modal, ModalProps, ModalWrapper } from '@/components/Modal';
import { cn } from '@/utils/classname';

// Generic form component interface for type safety
interface FormComponentProps<TData = unknown> {
  onSuccess: () => void;
  onClose?: () => void;
  initialData?: TData;
  [key: string]: unknown; // Allow additional props
}

// Union type for modal strategy
type ModalStrategy = 'url-state' | 'local-state';

// Base configuration for the editable section
interface BaseEditableSectionConfig {
  strategy: ModalStrategy;
  modalTitle: string;
  editButton?: Partial<ButtonProps> & {
    label?: string;
  };
  modal?: Partial<ModalProps>;
}

// URL-based modal configuration
interface UrlStateConfig extends BaseEditableSectionConfig {
  strategy: 'url-state';
  urlParam: string; // e.g., 'scan-level', 'schedule-edit'
}

// Local state modal configuration
interface LocalStateConfig extends BaseEditableSectionConfig {
  strategy: 'local-state';
  urlParam?: never; // Explicitly exclude urlParam for local state
}

// Discriminated union for type safety
type EditableSectionConfig = UrlStateConfig | LocalStateConfig;

// Props for render prop pattern
interface EditableSectionRenderProps {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

// Main component props using generics for form data typing
interface EditableSectionProps<TFormData = unknown> {
  config: EditableSectionConfig;
  children: (props: EditableSectionRenderProps) => React.ReactNode;
  formComponent: React.ComponentType<FormComponentProps<TFormData>>;
  formProps?: Omit<FormComponentProps<TFormData>, 'onSuccess' | 'onClose'>;
  onFormSuccess?: (data?: TFormData) => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * EditableSection Component
 *
 * A reusable component that consolidates edit button + modal patterns.
 * Supports both URL-based state management (for deep linking) and local state.
 * Uses render props pattern for maximum flexibility while maintaining type safety.
 *
 * @example URL State Strategy
 * <EditableSection
 *   config={{
 *     strategy: 'url-state',
 *     urlParam: 'scan-level',
 *     modalTitle: 'Edit Scan Level',
 *     editButton: { label: 'Edit', styleType: 'tertiary' }
 *   }}
 *   formComponent={ScanLevelSetupFormComponent}
 *   formProps={{ customerEmail }}
 *   onFormSuccess={handleSuccess}
 * >
 *   {({ openModal }) => (
 *     <div className="flex items-center gap-2">
 *       <span>Current Level: {currentLevel}</span>
 *       <Button onClick={openModal} label="Edit" />
 *     </div>
 *   )}
 * </EditableSection>
 *
 * @example Local State Strategy
 * <EditableSection
 *   config={{
 *     strategy: 'local-state',
 *     modalTitle: 'Edit Schedule',
 *     modal: { size: '5xl' }
 *   }}
 *   formComponent={ScanSchedule}
 *   formProps={{ customerEmail }}
 * >
 *   {({ openModal }) => (
 *     <Button onClick={openModal} label="Edit Schedule" />
 *   )}
 * </EditableSection>
 */
export function EditableSection<TFormData = unknown>({
  config,
  children,
  formComponent: FormComponent,
  formProps = {},
  onFormSuccess,
  className,
  'data-testid': dataTestId,
}: EditableSectionProps<TFormData>) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Modal state management based on strategy
  const isUrlBasedModal = config.strategy === 'url-state';
  const [localModalOpen, setLocalModalOpen] = React.useState(false);

  // Determine modal open state based on strategy
  const isModalOpen = isUrlBasedModal
    ? searchParams.get((config as UrlStateConfig).urlParam) !== null
    : localModalOpen;

  // Modal control functions
  const openModal = useCallback(() => {
    if (isUrlBasedModal) {
      setSearchParams(prev => {
        prev.set((config as UrlStateConfig).urlParam, 'open');
        return prev;
      });
    } else {
      setLocalModalOpen(true);
    }
  }, [isUrlBasedModal, config, setSearchParams]);

  const closeModal = useCallback(() => {
    if (isUrlBasedModal) {
      setSearchParams(prev => {
        prev.delete((config as UrlStateConfig).urlParam);
        return prev;
      });
    } else {
      setLocalModalOpen(false);
    }
  }, [isUrlBasedModal, config, setSearchParams]);

  // Form success handler
  const handleFormSuccess = useCallback(
    (data?: TFormData) => {
      closeModal();
      onFormSuccess?.(data);
    },
    [closeModal, onFormSuccess]
  );

  // Keyboard accessibility for URL-based modals
  useEffect(() => {
    if (isUrlBasedModal && isModalOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isUrlBasedModal, isModalOpen, closeModal]);

  const renderProps: EditableSectionRenderProps = {
    isOpen: isModalOpen,
    openModal,
    closeModal,
  };

  // Default edit button configuration
  const defaultEditButton: Partial<ButtonProps> = {
    label: 'Edit',
    styleType: 'tertiary',
    size: 'sm',
    startIcon: <PencilIcon className="size-4" />,
    className: 'h-fit',
    ...config.editButton,
  };

  return (
    <>
      <div className={cn(className)} data-testid={dataTestId}>
        {children(renderProps)}
      </div>

      {/* Render appropriate modal based on strategy */}
      {isUrlBasedModal ? (
        <Modal
          title={config.modalTitle}
          open={isModalOpen}
          onClose={closeModal}
          footer={{
            text: 'Apply',
            form: `editable-section-form-${(config as UrlStateConfig).urlParam}`,
          }}
          {...config.modal}
        >
          <FormComponent
            {...formProps}
            onSuccess={handleFormSuccess}
            onClose={closeModal}
          />
        </Modal>
      ) : (
        <ModalWrapper
          open={isModalOpen}
          onClose={closeModal}
          size={config.modal?.size || '5xl'}
          className={cn('px-8 py-6', config.modal?.className)}
        >
          <FormComponent
            {...formProps}
            onSuccess={handleFormSuccess}
            onClose={closeModal}
          />
        </ModalWrapper>
      )}
    </>
  );
}

// Convenience wrapper for simple edit button scenarios
interface SimpleEditableSectionProps<TFormData = unknown>
  extends Omit<EditableSectionProps<TFormData>, 'children'> {
  trigger?: React.ReactNode;
  displayValue?: React.ReactNode;
  label?: string;
}

export function SimpleEditableSection<TFormData = unknown>({
  trigger,
  displayValue,
  label = 'Edit',
  config,
  ...props
}: SimpleEditableSectionProps<TFormData>) {
  return (
    <EditableSection config={config} {...props}>
      {({ openModal }) => (
        <div className="flex items-center gap-2">
          {displayValue}
          {trigger ? (
            <div onClick={openModal} role="button" tabIndex={0}>
              {trigger}
            </div>
          ) : (
            <Button
              onClick={openModal}
              label={label}
              styleType="tertiary"
              size="sm"
              startIcon={<PencilIcon className="size-4" />}
              className="h-fit text-base"
            />
          )}
        </div>
      )}
    </EditableSection>
  );
}

// Hook for external modal control (advanced usage)
export function useEditableSection(
  config: Pick<EditableSectionConfig, 'strategy' | 'urlParam'>
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localModalOpen, setLocalModalOpen] = React.useState(false);

  const isUrlBasedModal = config.strategy === 'url-state';

  const isModalOpen = isUrlBasedModal
    ? searchParams.get((config as UrlStateConfig).urlParam!) !== null
    : localModalOpen;

  const openModal = useCallback(() => {
    if (isUrlBasedModal && 'urlParam' in config && config.urlParam) {
      const urlParam = config.urlParam;
      setSearchParams(prev => {
        prev.set(urlParam, 'open');
        return prev;
      });
    } else {
      setLocalModalOpen(true);
    }
  }, [isUrlBasedModal, config, setSearchParams]);

  const closeModal = useCallback(() => {
    if (isUrlBasedModal && 'urlParam' in config && config.urlParam) {
      const urlParam = config.urlParam;
      setSearchParams(prev => {
        prev.delete(urlParam);
        return prev;
      });
    } else {
      setLocalModalOpen(false);
    }
  }, [isUrlBasedModal, config, setSearchParams]);

  return { isModalOpen, openModal, closeModal };
}
